import type {FillBoxSize, GradientFillObject, GradientStop, RadialGradientFillObject} from '@PosterWhiteboard/classes/fill.types';
import {FillTypes, RadialGradientShape, SQUARE_FILL_BOX} from '@PosterWhiteboard/classes/fill.types';
import {rgbaToRGBAString} from '@Utils/color.util';
import {assertNever} from '@Utils/assert-never.util';

const PERCENT = 100;

/**
 * The CSS background that draws the gradient exactly as the canvas does in a box of the given proportions: a linear gradient's line runs corner to
 * corner along the angle in both, and a radial circle's radius is a share of the longer side, which CSS only expresses per axis.
 */
export const fillValueToCss = (fill: GradientFillObject, box: FillBoxSize = SQUARE_FILL_BOX): string => {
  const stops = getCssColorStops(fill.stops);
  switch (fill.fillType) {
    case FillTypes.LINEAR_GRADIENT:
      return `linear-gradient(${fill.angle}deg, ${stops})`;
    case FillTypes.RADIAL_GRADIENT: {
      const {horizontalRadius, verticalRadius} = getCssRadialRadii(fill, box);
      return `radial-gradient(${horizontalRadius}% ${verticalRadius}% at ${fill.center.x * PERCENT}% ${fill.center.y * PERCENT}%, ${stops})`;
    }
    default:
      return assertNever(fill);
  }
};

interface CssRadialRadii {
  horizontalRadius: number;
  verticalRadius: number;
}

const getCssRadialRadii = (fill: RadialGradientFillObject, box: FillBoxSize): CssRadialRadii => {
  if (fill.shape === RadialGradientShape.ELLIPSE) {
    return {horizontalRadius: fill.radius * PERCENT, verticalRadius: fill.radius * PERCENT};
  }
  const radius = fill.radius * Math.max(box.width, box.height);
  return {horizontalRadius: (radius / box.width) * PERCENT, verticalRadius: (radius / box.height) * PERCENT};
};

const getCssColorStops = (stops: GradientStop[]): string => {
  return stops
    .map((stop) => {
      return `${rgbaToRGBAString(stop.color)} ${stop.offset * PERCENT}%`;
    })
    .join(', ');
};
